import { Container, Loader, Paper, SimpleGrid, Table, Text, Title } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { PageHeader } from "@/components/PageHeader"
import { queries } from "@/lib/queries"

const PLAYFAIR = "'Playfair Display', Georgia, serif"

export const Route = createFileRoute("/companies/$id/budget")({
  component: CompanyBudget,
})

function CompanyBudget() {
  const { id } = Route.useParams()
  const { data, isPending, isError } = useQuery(queries.companyBudget(id))

  if (isPending) return <Loader />
  if (isError) return <Text c="var(--pitch-negative)">Failed to load budget</Text>

  return (
    <Container size="xl">
      <PageHeader eyebrow="Fund Application">{id.toUpperCase()}</PageHeader>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="xl">
        <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
          <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
            Allocation
          </Text>
          <Text
            ff={PLAYFAIR}
            fz={26}
            fw={500}
            c="var(--pitch-text-primary)"
            mt={6}
            className="pitch-tabular"
            style={{ lineHeight: 1.1 }}
          >
            ${data.allocation.toFixed(1)}M
          </Text>
        </Paper>
        <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
          <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
            Deployed
          </Text>
          <Text
            ff={PLAYFAIR}
            fz={26}
            fw={500}
            c="var(--pitch-text-primary)"
            mt={6}
            className="pitch-tabular"
            style={{ lineHeight: 1.1 }}
          >
            ${data.deployed.toFixed(1)}M
          </Text>
        </Paper>
        <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
          <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
            Remaining
          </Text>
          <Text
            ff={PLAYFAIR}
            fz={26}
            fw={500}
            c="var(--pitch-text-primary)"
            mt={6}
            className="pitch-tabular"
            style={{ lineHeight: 1.1 }}
          >
            ${data.remaining.toFixed(1)}M
          </Text>
        </Paper>
      </SimpleGrid>

      <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
        <Title
          order={4}
          ff={PLAYFAIR}
          fw={500}
          c="var(--pitch-text-primary)"
          mb="sm"
          style={{ letterSpacing: "-0.005em" }}
        >
          Quarterly Deployment
        </Title>
        {data.quarterly.length === 0 ? (
          <Text c="var(--pitch-text-tertiary)" size="sm">
            No deployment data yet
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Quarter</Table.Th>
                <Table.Th>Amount ($M)</Table.Th>
                <Table.Th>Cumulative ($M)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.quarterly.map((q) => (
                <Table.Tr key={q.quarter}>
                  <Table.Td>{q.quarter}</Table.Td>
                  <Table.Td className="pitch-tabular">{q.amountM}</Table.Td>
                  <Table.Td className="pitch-tabular">{q.cumulativeM}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Container>
  )
}
